import { useMemo, useState } from 'react';

import { db } from '../../firebase/config.js';
import useSharedCollectionWhere from '../../firebase/useSharedCollectionWhere.js';
import unplannedSubtasksFor from '../../helpers/adressHelpers/unplannedSubtasksFor.js';
import { SUBTASK_TYPE, isAppointment } from '../../helpers/adressHelpers/taskKind.js';

/**
 * De klussen van dit huishouden die nog bij geen enkele afspraak horen, met
 * per klus een knop om hem bij déze afspraak in te plannen. Staat onder de
 * subtaken-tabel in de afspraakbewerker.
 *
 * Alleen bij een huisbezoek mét huishouden: een meetmoment of roosterregel
 * heeft geen huishouden om klussen van te tonen. En alleen als de afspraak al
 * in de database staat (`docid`): in de aanmaakmodus van de agenda staat dit
 * tabblad er al vóór het opslaan, en een klik zou dan een bestaande klus
 * koppelen aan een afspraak die bij annuleren nooit ontstaat — precies de
 * wees ('Afspraak verwijderd') die de opruiming van 2026-09-30 wegwerkte.
 *
 * Staat er niets, dan staat er ook NIETS — zelfde afweging als het lijstje
 * onder de details: een kopje met 'geen losse klussen' maakt elke afspraak
 * langer zonder iets te vertellen. Ook tijdens het laden niets: de tabel
 * erboven staat er al, en een blok dat daarna omslaat leest als een fout.
 *
 * Inplannen zet alleen `Parent`; de subtaak verschijnt daarna vanzelf in de
 * tabel erboven, want die luistert op `Parent == deze afspraak`.
 * AI-CONTEXT: See docs/features/subtaken-bij-het-huishouden.md
 *
 * @param {object} props
 * @param {{ID?: string, docid?: string, Type?: string, Parent?: string|null, AdressID?: string}} props.appointment - De afspraak die open staat; zonder `docid` is hij nog niet opgeslagen
 * @param {boolean} props.canEdit - Of de gebruiker taken mag bewerken; zonder recht alleen de lijst
 * @returns {JSX.Element|null}
 */
const UnplannedSubtasks = ({ appointment, canEdit }) => {
    const subtasks = useSharedCollectionWhere('Tasks', 'Type', SUBTASK_TYPE);
    const [fout, setFout] = useState('');

    const opgeslagen = Boolean(appointment?.docid);
    const adressId = opgeslagen && isAppointment(appointment) ? (appointment.AdressID || '') : '';
    const losse = useMemo(() => unplannedSubtasksFor(subtasks, adressId), [subtasks, adressId]);

    if (losse.length === 0) return null;

    /**
     * @param {object} subtask
     */
    const plan = async (subtask) => {
        setFout('');
        try {
            await db.collection('Tasks').doc(subtask.docid).update({ Parent: appointment.ID });
        } catch (e) {
            console.error('UnplannedSubtasks: inplannen mislukt', e);
            setFout('Inplannen is niet gelukt. Probeer het opnieuw.');
        }
    };

    return (
        <div className="unplanned-subtasks">
            <p className="unplanned-subtasks__title">Nog niet ingeplande klussen bij dit huishouden</p>
            <ul className="unplanned-subtasks__list">
                {losse.map((subtask) => (
                    <li key={subtask.ID} className="unplanned-subtasks__item">
                        <span>{subtask.Title || 'Naamloze taak'}</span>
                        {canEdit && (
                            <button type="button" className="unplanned-subtasks__plan" onClick={() => plan(subtask)}>
                                Inplannen bij deze afspraak
                            </button>
                        )}
                    </li>
                ))}
            </ul>
            {fout && <p className="unplanned-subtasks__error" role="alert">{fout}</p>}
        </div>
    );
};

export default UnplannedSubtasks;
